import React from "react";
import { TrendingUp, Clock, Target, Users } from "lucide-react";
import DashboardWidget from "@/components/DashboardWidget";
import { Badge } from "@/components/ui/badge";

interface PerformanceMetricsWidgetProps {
  stats: any;
}

export const PerformanceMetricsWidget: React.FC<
  PerformanceMetricsWidgetProps
> = ({ stats }) => {
  // Mock performance data (in real implementation, this would come from your analytics)
  const performanceData = {
    bounceRate: 34.2,
    avgSessionDuration: "2m 45s",
    conversionRate: 5.8,
    pageLoadTime: 1.2,
    returnVisitorRate: 42.3,
    pageViews: stats?.totalPageViews || 1247,
    newVsReturning: {
      new: 57.7,
      returning: 42.3,
    },
  };

  const metrics = [
    {
      title: "Bounce Rate",
      value: `${performanceData.bounceRate}%`,
      change: -2.1,
      icon: TrendingUp,
      description: "Users leaving after viewing one page",
      status: "good" as const,
    },
    {
      title: "Avg Session",
      value: performanceData.avgSessionDuration,
      change: +0.3,
      icon: Clock,
      description: "Average time spent on site",
      status: "good" as const,
    },
    {
      title: "Conversion Rate",
      value: `${performanceData.conversionRate}%`,
      change: +1.2,
      icon: Target,
      description: "Visitors completing desired actions",
      status: "excellent" as const,
    },
    {
      title: "Return Visitors",
      value: `${performanceData.returnVisitorRate}%`,
      change: +3.8,
      icon: Users,
      description: "Users returning to your site",
      status: "good" as const,
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "excellent":
        return "text-green-600 bg-green-50";
      case "good":
        return "text-blue-600 bg-blue-50";
      case "warning":
        return "text-yellow-600 bg-yellow-50";
      case "poor":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getChangeColor = (change: number) => {
    return change >= 0 ? "text-green-600" : "text-red-600";
  };

  return (
    <DashboardWidget id="performance-metrics" title="Performance Metrics">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div
              key={metric.title}
              className="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className={`p-2 rounded-lg ${getStatusColor(metric.status)}`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <Badge
                  variant={metric.change >= 0 ? "default" : "destructive"}
                  className="text-xs"
                >
                  {metric.change >= 0 ? "+" : ""}
                  {metric.change}%
                </Badge>
              </div>
              <div className="space-y-1">
                <h3 className="font-semibold text-gray-900">{metric.title}</h3>
                <div className="text-2xl font-bold text-gray-900">
                  {metric.value}
                </div>
                <p className="text-sm text-gray-600">{metric.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border">
          <h4 className="font-semibold text-gray-900 mb-2">
            Page Load Performance
          </h4>
          <div className="text-2xl font-bold text-indigo-600 mb-1">
            {performanceData.pageLoadTime}s
          </div>
          <p className="text-sm text-gray-600">Average page load time</p>
          <div className="mt-2">
            <div className="bg-white rounded-full h-2">
              <div className="bg-indigo-500 h-2 rounded-full w-[85%]"></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              85% faster than average
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border">
          <h4 className="font-semibold text-gray-900 mb-2">Visitor Segments</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">New Visitors</span>
              <span className="font-semibold text-emerald-600">
                {performanceData.newVsReturning.new}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Returning</span>
              <span className="font-semibold text-emerald-600">
                {performanceData.newVsReturning.returning}%
              </span>
            </div>
            <div className="bg-white rounded-full h-2 flex overflow-hidden">
              <div
                className="bg-emerald-500 h-2"
                style={{ width: `${performanceData.newVsReturning.new}%` }}
              ></div>
              <div
                className="bg-emerald-300 h-2"
                style={{
                  width: `${performanceData.newVsReturning.returning}%`,
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 text-sm text-gray-600">
        <p>• Performance metrics update every 15 minutes</p>
        <p>• Percentages show change compared to previous period</p>
      </div>
    </DashboardWidget>
  );
};

export default PerformanceMetricsWidget;
